<template>
  <section
    v-if="equivalentProducts && equivalentProducts.length > 0"
    class="mt-6 pt-6 md:mt-8 md:pt-8 border-t border-border"
    v-cloak
  >
    <!-- Cabecera -->
    <div class="flex items-center justify-between md:mb-8 mb-4">
      <h2 class="text-2xl font-bold text-text-main">
        {{ transPeralbaPro.item897 }}
      </h2>
    </div>

    <!-- Contenedor del Slider -->
    <div class="relative -mx-4 px-4 lg:-mx-0 lg:px-0">
      <!-- Referencia 'sliderRef' añadida para el control JS -->
      <div
        ref="sliderEquivalentProducts"
        class="flex overflow-x-auto gap-5 pb-5 snap-x scrollbar-hide px-2 scroll-smooth"
      >
        <button
          @click="scrollSlider('left')"
          class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 size-10 rounded-full border border-border bg-white text-text-secondary hover:border-primary hover:text-primary items-center justify-center transition-colors shadow-lg disabled:opacity-50 hidden md:flex"
        >
          <vue-icon name="chevron_left" class="text-xl" />
        </button>
        <button
          @click="scrollSlider('right')"
          class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 size-10 rounded-full border border-border bg-white text-text-secondary hover:border-primary hover:text-primary items-center justify-center transition-colors shadow-lg hidden md:flex"
        >
          <vue-icon name="chevron_right" class="text-xl" />
        </button>
        <a
          v-for="product in equivalentProducts"
          :key="product.id"
          :href="product.href"
          class="flex-none w-[280px] snap-start group block bg-white border border-border rounded-lg p-4 hover:shadow-lg hover:border-primary/50 transition-all duration-300 relative"
        >
          <!-- Imagen -->
          <div
            class="relative aspect-square bg-surface rounded-md mb-4 overflow-hidden flex items-center justify-center"
          >
            <img
              :alt="product.translation.name"
              loading="lazy"
              :style="vtStyle(product)"
              class="w-3/4 h-3/4 object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-110"
              :src="
                product.images[0].thumbnail
                  ? product.images[0].thumbnail
                  : noImagePath
              "
            />

            <button
              v-if="userIsProfecional"
              @click.stop.prevent="
                openWishlistBridge(product.id, wishlistQty(product))
              "
              class="absolute top-2 left-2 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-primary hover:text-white text-text-secondary transition-colors shadow-sm z-20 backdrop-blur-sm"
              :aria-label="transPeralbaPro.item1416"
            >
              <vue-icon name="bookmark" class="text-[18px]" />
            </button>

            <button
              v-else
              @click.stop.prevent="openLoginBridge"
              class="absolute top-2 left-2 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-primary hover:text-white text-text-secondary transition-colors shadow-sm z-20 backdrop-blur-sm"
              :aria-label="transPeralbaPro.item1416"
            >
              <vue-icon name="bookmark" class="text-[18px]" />
            </button>

            <!-- Marca -->
            <div
              class="absolute bottom-1 left-1 flex items-center gap-3 text-[#111418] border border-border rounded-lg px-2 py-1"
            >
              <img
                loading="lazy"
                :src="'/uploads/thumbnail-brands/' + product.brand.logo_image"
                :alt="product.brand.name"
                class="max-h-[20px] w-auto"
              />
            </div>
          </div>

          <!-- Contenido -->
          <div class="space-y-1">
            <!-- REF -->
            <p class="text-xs text-text-secondary font-medium truncate">
              REF: {{ product.alias_code || product.erp_reference }}
            </p>

            <!-- Título -->
            <p
              class="text-sm font-bold text-text-main leading-snug group-hover:text-primary transition-colors min-h-[2.5em]"
            >
              {{ product.translation.name }}
            </p>

            <!-- Footer: Precio y Botón -->

            <div
              class="flex flex-col pt-2 mt-2 border-t border-dashed border-border gap-2"
            >
              <div class="flex flex-col gap-1">
                <template v-if="parseFloat(product.discount_) > 0">
                  <div class="flex gap-2 items-center">
                    <span
                      class="text-xs text-text-secondary line-through decoration-primary"
                    >
                      {{
                        currency(parseFloat(product.price_without_discount_))
                      }}
                    </span>
                    <span
                      class="bg-primary-50 text-primary text-xs font-bold px-2 py-0.5 rounded"
                    >
                      -{{ percentage(parseFloat(product.discount_)) }}
                    </span>
                  </div>
                  <span class="text-lg font-bold text-red-600">
                    {{ currency(parseFloat(product.price_)) }}
                  </span>
                </template>

                <!-- Caso: Precio Normal -->
                <template v-else>
                  <span class="text-lg font-bold text-text-main">
                    {{ currency(parseFloat(product.price)) }}
                  </span>
                </template>
              </div>

              <div class="flex items-center justify-between gap-2">
                <div
                  class="input-quantity-div flex items-center border border-border rounded-md bg-surface h-8"
                >
                  <button
                    @click.prevent="decreaseQty(product)"
                    :aria-label="transPeralbaPro.item1418"
                    :disabled="product.custom_quantity <= getMinQty(product)"
                    type="button"
                    class="btn-qty minus w-6 h-full flex items-center justify-center hover:bg-gray-200 text-gray-600 rounded-l-md"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="16px"
                      viewBox="0 -960 960 960"
                      width="16px"
                      fill="currentColor"
                    >
                      <path d="M200-440v-80h560v80H200Z"></path>
                    </svg>
                  </button>

                  <input
                    v-model.number="product.custom_quantity"
                    @change="validateInput(product)"
                    type="number"
                    class="input-quantity [appearance:textfield] [&amp;::-webkit-outer-spin-button]:appearance-none [&amp;::-webkit-inner-spin-button]:appearance-none w-10 h-full text-center text-sm bg-transparent border-none focus:ring-0 p-0 appearance-none remove-arrow"
                    :min="getMinQty(product)"
                    :step="getMinQty(product)"
                  />

                  <button
                    @click.prevent="increaseQty(product)"
                    :aria-label="transPeralbaPro.item1417"
                    type="button"
                    class="btn-qty plus w-6 h-full flex items-center justify-center hover:bg-gray-200 text-gray-600 rounded-r-md"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="16px"
                      viewBox="0 -960 960 960"
                      width="16px"
                      fill="currentColor"
                    >
                      <path
                        d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"
                      ></path>
                    </svg>
                  </button>
                </div>

                <button
                  @click.prevent="addToCart(product)"
                  class="btn-wishlist-action border border-border rounded-md bg-surface flex items-center justify-center gap-2 hover:bg-primary hover:text-white px-4 py-1.5 transition-colors duration-200 font-medium text-sm group/btn"
                  :title="transPeralbaPro.item896"
                >
                  <vue-icon
                    name="add_shopping_cart"
                    class="text-[18px] group-hover/btn:scale-110 transition-transform"
                  />
                  <span>{{ transPeralbaPro.item1277 }}</span>
                </button>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  </section>
</template>

<script>
import {
  nextQuantity,
  normalizeQuantity,
  previousQuantity,
  saleStep,
} from "@/utils/quantity";

export default {
  name: "EquivalentProducts",
  emits: ["open-wishlist", "open-login", "add-to-cart"],
  // Definimos las propiedades que este componente recibirá del padre
  props: {
    userIsProfecional: {type: Boolean, required: true, default: false},
    noImagePath: {type: String, required: true},
    equivalentProducts: {
      type: Array,
      required: true,
      default: () => [],
    },
    transPeralbaPro: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  methods: {
    // View transition card -> ficha: mismo nombre que genera la ficha destino
    // ($vtName en details.blade.php) y las cards del buscador (ProductItem.vue):
    // vt-<alias_code o erp_reference> saneado a custom-ident valido.
    vtStyle(product) {
      const ref = product.alias_code || product.erp_reference;
      if (!ref) return {};
      return {
        "view-transition-name":
          "vt-" + String(ref).replace(/[^A-Za-z0-9_-]/g, "-"),
        "view-transition-class": "vt-product-image",
      };
    },

    // Control del slider horizontal
    scrollSlider(direction) {
      const container = this.$refs.sliderEquivalentProducts;
      if (!container) return;

      const scrollAmount = 300;
      if (direction === "left") {
        container.scrollLeft -= scrollAmount;
      } else {
        container.scrollLeft += scrollAmount;
      }
    },

    getMinQty(product) {
      return saleStep(product);
    },
    increaseQty(product) {
      product.custom_quantity = nextQuantity(
        product.custom_quantity,
        saleStep(product)
      );
    },
    decreaseQty(product) {
      product.custom_quantity = previousQuantity(
        product.custom_quantity,
        saleStep(product)
      );
    },
    // Quantitat escrita a ma: la puja al multiple de venda mes proper
    // (multiple 4, escriu 10 => 12). Veure resources/js/utils/quantity.js.
    validateInput(product) {
      product.custom_quantity = normalizeQuantity(
        product.custom_quantity,
        saleStep(product)
      );
    },

    // La llista de desitjos rep la mateixa quantitat valida que la cistella.
    wishlistQty(product) {
      return normalizeQuantity(
        product.custom_quantity || product.quantity,
        saleStep(product)
      );
    },

    openWishlistBridge(id, qty) {
      this.$emit("open-wishlist", id, qty);
    },
    openLoginBridge() {
      this.$emit("open-login");
    },
    addToCart(product) {
      this.$emit("add-to-cart", product);
    },

    // Antes eran `filters` locales (Vue 2, que ya no existen en Vue 3):
    // mismos nombres y mismos cuerpos, ahora metodos que la plantilla llama
    // explicitamente. Su espejo en PHP es $fmtEs (details.blade.php).
    currency(value) {
      if (value === null || value === undefined) return "0,00 €";

      // Convertim a número per si arriba com a string
      const number = parseFloat(value);

      return (
        new Intl.NumberFormat("es-ES", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(number) + " €"
      );
    },
    percentage(value) {
      if (value === null || value === undefined) return "0,00 %";

      // Convertim a número per si arriba com a string
      const number = parseFloat(value);

      return (
        new Intl.NumberFormat("es-ES", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(number) + " %"
      );
    },
  },
};
</script>
